/*
 * Cité Érotik — global theme behaviours.
 * Lightweight vanilla JS + Web Components. No framework runtime.
 */
(function () {
  'use strict';

  window.theme = window.theme || {};

  /* ---------- Money formatting ---------- */
  theme.formatMoney = function (cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var value = '';
    var fmt = format || theme.moneyFormat || '${{amount}}';
    var amount = (cents / 100).toFixed(2);
    var parts = amount.split('.');
    var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,');
    value = dollars + '.' + parts[1];
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, value);
  };

  /* ---------- Body scroll lock ---------- */
  var lockCount = 0;
  theme.lockScroll = function () {
    lockCount++;
    document.body.style.overflow = 'hidden';
  };
  theme.unlockScroll = function () {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) document.body.style.overflow = '';
  };

  /* ---------- Generic panel (slide-in) ---------- */
  theme.openPanel = function (panel, scrim, transformClass) {
    if (!panel) return;
    panel.classList.remove(transformClass);
    panel.setAttribute('aria-hidden', 'false');
    if (scrim) scrim.classList.add('is-open');
    theme.lockScroll();
  };
  theme.closePanel = function (panel, scrim, transformClass) {
    if (!panel) return;
    panel.classList.add(transformClass);
    panel.setAttribute('aria-hidden', 'true');
    if (scrim) scrim.classList.remove('is-open');
    theme.unlockScroll();
  };

  /* ---------- Scroll-reveal slide-in animations ---------- */
  (function () {
    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var supported = 'IntersectionObserver' in window;

    // Gate the hiding CSS on this class so no-JS / unsupported = always visible.
    if (!supported || prefersReduced) return;
    document.documentElement.classList.add('reveal-ready');

    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

    function observeAll(root) {
      (root || document).querySelectorAll('[data-reveal]:not(.is-revealed)').forEach(function (el) {
        // Stagger siblings sharing a [data-reveal-group] parent for a nicer cascade.
        var group = el.closest('[data-reveal-group]');
        if (group && !el.style.getPropertyValue('--reveal-delay')) {
          var sibs = Array.prototype.slice.call(group.querySelectorAll('[data-reveal]'));
          var i = sibs.indexOf(el);
          if (i > 0) el.style.setProperty('--reveal-delay', Math.min(i, 6) * 90 + 'ms');
        }
        observer.observe(el);
      });
    }

    observeAll(document);

    // Re-scan when sections are re-rendered in the theme editor.
    document.addEventListener('shopify:section:load', function (e) { observeAll(e.target); });
  })();

  /* ---------- Sticky header shadow ---------- */
  var stickyHeader = document.querySelector('[data-sticky-header]');
  if (stickyHeader) {
    var onScroll = function () {
      if (window.scrollY > 8) {
        stickyHeader.classList.add('shadow-elevation-2');
      } else {
        stickyHeader.classList.remove('shadow-elevation-2');
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ---------- Search panel ---------- */
  (function () {
    var panel = document.querySelector('[data-search-panel]');
    var scrim = document.querySelector('[data-search-scrim]');
    var input = document.getElementById('header-search-input');
    if (!panel) return;

    function open() {
      theme.openPanel(panel, scrim, '-translate-y-full');
      if (input) setTimeout(function () { input.focus(); }, 100);
    }
    function close() {
      theme.closePanel(panel, scrim, '-translate-y-full');
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-search-open]')) { e.preventDefault(); open(); }
      else if (e.target.closest('[data-search-close]') || e.target.closest('[data-search-scrim]')) { close(); }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  })();

  /* ---------- Mobile menu drawer ---------- */
  class MenuDrawer extends HTMLElement {
    connectedCallback() {
      this.panel = this.querySelector('[data-menu-panel]');
      this.scrim = this.querySelector('[data-menu-scrim]');

      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-menu-open]')) { e.preventDefault(); this.open(); }
        else if (e.target.closest('[data-menu-close]') || e.target.closest('[data-menu-scrim]')) { this.close(); }
      });
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
    }
    open() { theme.openPanel(this.panel, this.scrim, '-translate-x-full'); }
    close() { theme.closePanel(this.panel, this.scrim, '-translate-x-full'); }
  }
  if (!customElements.get('menu-drawer')) customElements.define('menu-drawer', MenuDrawer);

  /* ---------- Slideshow carousel ---------- */
  class SlideshowCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slides]');
      this.slides = Array.from(this.querySelectorAll('[data-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-slide-dot]'));
      this.index = 0;
      if (this.slides.length < 2) return;

      this.querySelector('[data-slide-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-slide-next]')?.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(parseInt(dot.dataset.slideDot, 10))));

      this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      this.updateDots();

      if (this.dataset.autoplay === 'true') {
        var speed = parseInt(this.dataset.speed, 10) || 5000;
        this.timer = setInterval(() => this.go(this.index + 1), speed);
        this.addEventListener('mouseenter', () => clearInterval(this.timer));
      }
    }
    go(i) {
      var count = this.slides.length;
      this.index = ((i % count) + count) % count;
      this.track.scrollTo({ left: this.slides[this.index].offsetLeft, behavior: 'smooth' });
      this.updateDots();
    }
    onScroll() {
      var i = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (i !== this.index) { this.index = i; this.updateDots(); }
    }
    updateDots() {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('bg-white', i === this.index);
        dot.classList.toggle('w-6', i === this.index);
        dot.classList.toggle('bg-white/50', i !== this.index);
      });
    }
  }
  if (!customElements.get('slideshow-carousel')) customElements.define('slideshow-carousel', SlideshowCarousel);
})();
